import type { PanelArtifact } from '../../../../../bridge/types/messages.ts';
import { getContentArtifactCardType } from '../../../../../bridge/utils/artifactDisplayCopy.ts';

import { ArtifactCard } from './ArtifactCard.tsx';
import { CarouselArtifactCard } from './CarouselArtifactCard.tsx';
import { ContentArtifactCard } from './ContentArtifactCard.tsx';
import { MessageRow } from './MessageRow.tsx';
import { getPanelIcon } from './panelTypeIcons.tsx';
import { CampaignSetupPanel } from './setup/CampaignSetupPanel.tsx';
import { SocialPostPreview } from './SocialPostPreview.tsx';

export const PanelArtifactMessage = ({
	message,
	onClick,
	canBuildCarousel = true,
}: {
	message: PanelArtifact;
	onClick: () => void;
	/** False once the customer has sent another message, so the offer does not
	 * drop an unrelated request into a conversation that has moved on. */
	canBuildCarousel?: boolean;
}) => {
	const sourcePost = message.source_post;

	if (message.artifact_type === 'carousel') {
		return <CarouselArtifactCard message={message} onClick={onClick} />;
	}

	// A shared post previews in the thread and plays there. An inspiration has no
	// panel, so this is the ONLY way one renders — never the flat card below,
	// whose whole row is a button that used to slide a panel over the
	// conversation the post was shared in. A message written before source_post
	// existed carries no media, and the caption alone is still the honest render.
	if (message.artifact_type === 'inspiration_reference') {
		return (
			<MessageRow data-testid={`panel-artifact-${message.id}`}>
				<SocialPostPreview
					message={message}
					sourceUrl={sourcePost?.url ?? ''}
					canBuildCarousel={canBuildCarousel}
					autoPlay
				/>
			</MessageRow>
		);
	}

	const artifactType = getContentArtifactCardType(message.artifact_type);
	if (artifactType) {
		return (
			<ContentArtifactCard
				message={{
					...message,
					artifact_type: artifactType,
				}}
				onClick={onClick}
			/>
		);
	}

	// A process/campaign has no leaf artifact_type, so it would otherwise fall
	// through to the flat card below and lose its per-step rows. Render the
	// step-aware panel instead — it self-fetches its snapshot from item_id.
	if (message.panel_type === 'process') {
		return (
			<CampaignSetupPanel
				message={{
					type: 'process_setup_panel',
					id: message.id,
					process_id: message.item_id,
				}}
				onClick={onClick}
				icon={getPanelIcon(message.artifact_type ?? message.panel_type)}
			/>
		);
	}

	return (
		<ArtifactCard
			icon={getPanelIcon(message.artifact_type ?? message.panel_type)}
			title={message.title ?? 'Review Item'}
			description={message.description ?? 'A draft is ready for your review.'}
			onClick={onClick}
		/>
	);
};
